import React, { useMemo } from 'react'
import { Icon } from '../../atoms/Icon'
import { AdvancedDropdown } from '../AdvancedDropdown'
import { DropdownOption } from '../AdvancedDropdown/AdvancedDropdown.types'
import {
  TextStyleDropdownProps,
  TextStyle,
  TextStyleType,
} from './TextStyleDropdown.types'
import {
  StyledTextStyleOption,
  StyledPreviewText,
  StyledStyleLabel,
} from './TextStyleDropdown.styles'
import { theme } from '../../../theme'

const defaultTextStyles: Record<TextStyleType, TextStyle> = {
  heading1: {
    type: 'heading1',
    label: 'Heading 1',
    fontSize: theme.typography.fontSize.hero,
    fontWeight: theme.typography.fontWeight.bold,
    lineHeight: theme.typography.lineHeight.tight,
    tag: 'h1',
    icon: 'text-h-one',
  },
  heading2: {
    type: 'heading2',
    label: 'Heading 2',
    fontSize: theme.typography.fontSize.title,
    fontWeight: theme.typography.fontWeight.semibold,
    lineHeight: theme.typography.lineHeight.tight,
    tag: 'h2',
    icon: 'text-h-two',
  },
  heading3: {
    type: 'heading3',
    label: 'Heading 3',
    fontSize: theme.typography.fontSize.section,
    fontWeight: theme.typography.fontWeight.semibold,
    lineHeight: theme.typography.lineHeight.normal,
    tag: 'h3',
    icon: 'text-h-three',
  },
  paragraph: {
    type: 'paragraph',
    label: 'Paragraph',
    fontSize: theme.typography.fontSize.body,
    fontWeight: theme.typography.fontWeight.normal,
    lineHeight: theme.typography.lineHeight.relaxed,
    tag: 'p',
    icon: 'text-t',
  },
  quote: {
    type: 'quote',
    label: 'Quote',
    fontSize: theme.typography.fontSize.body,
    fontWeight: theme.typography.fontWeight.medium,
    lineHeight: theme.typography.lineHeight.relaxed,
    tag: 'blockquote',
    icon: 'quotes',
  },
  code: {
    type: 'code',
    label: 'Code',
    fontSize: theme.typography.fontSize.small,
    fontWeight: theme.typography.fontWeight.normal,
    lineHeight: theme.typography.lineHeight.normal,
    tag: 'code',
    icon: 'code',
  },
  'list-ordered': {
    type: 'list-ordered',
    label: 'Ordered List',
    fontSize: theme.typography.fontSize.body,
    fontWeight: theme.typography.fontWeight.normal,
    lineHeight: theme.typography.lineHeight.relaxed,
    tag: 'ol',
    icon: 'list-numbers',
  },
  'list-unordered': {
    type: 'list-unordered',
    label: 'Unordered List',
    fontSize: theme.typography.fontSize.body,
    fontWeight: theme.typography.fontWeight.normal,
    lineHeight: theme.typography.lineHeight.relaxed,
    tag: 'ul',
    icon: 'list-bullets',
  },
}

export const TextStyleDropdown: React.FC<TextStyleDropdownProps> = ({
  value = 'paragraph',
  onChange,
  availableStyles,
  disabled = false,
  size = 'medium',
  className,
  width = 200,
  customStyles = {},
}) => {
  const textStyles = useMemo(() => {
    const mergedStyles = { ...defaultTextStyles }

    // Merge custom styles
    Object.entries(customStyles).forEach(([type, customStyle]) => {
      if (mergedStyles[type as TextStyleType]) {
        mergedStyles[type as TextStyleType] = {
          ...mergedStyles[type as TextStyleType],
          ...customStyle,
        }
      }
    })

    return mergedStyles
  }, [customStyles])

  const options = useMemo((): DropdownOption[] => {
    const stylesToShow =
      availableStyles || (Object.keys(textStyles) as TextStyleType[])

    return stylesToShow.map((styleType) => {
      const style = textStyles[styleType]
      return {
        id: style.type,
        label: style.label,
        icon: style.icon ? (
          <Icon name={style.icon as any} size="small" />
        ) : undefined,
      }
    })
  }, [availableStyles, textStyles])

  const renderOption = (option: DropdownOption) => {
    const style = textStyles[option.id as TextStyleType]

    return (
      <StyledTextStyleOption>
        <StyledPreviewText
          $fontSize={style.fontSize || theme.typography.fontSize.body}
          $fontWeight={style.fontWeight || theme.typography.fontWeight.normal}
          $lineHeight={style.lineHeight || theme.typography.lineHeight.normal}
        >
          {style.label}
        </StyledPreviewText>
        <StyledStyleLabel>{style.label}</StyledStyleLabel>
      </StyledTextStyleOption>
    )
  }

  const handleChange = (selectedValue: string | string[]) => {
    const styleType = selectedValue as TextStyleType
    if (onChange && textStyles[styleType]) {
      onChange(styleType, textStyles[styleType])
    }
  }

  const currentStyle = textStyles[value]

  return (
    <AdvancedDropdown
      value={value}
      onChange={handleChange}
      options={options}
      disabled={disabled}
      size={size}
      className={className}
      width={width}
      renderOption={renderOption}
      renderValue={() => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.small,
          }}
        >
          {currentStyle.icon && (
            <Icon name={currentStyle.icon as any} size="small" />
          )}
          <span>{currentStyle.label}</span>
        </div>
      )}
      placeholder="Select text style"
      searchable={false}
      position="bottom"
    />
  )
}
